import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";

const splitToBoxes = (value, length) =>
  Array.from({ length }, (_, i) => value[i] ?? "");

/**
 * Segmented one-time-code input: one box per digit, with paste-the-whole-code,
 * auto-advance, backspace-to-previous and arrow-key navigation. Numeric only.
 * Styled to match the auth design system (see login.scss → .otp-code).
 *
 * The boxes are held in local state so an empty box keeps its position while
 * editing — clearing or replacing an interior digit leaves that slot in place
 * instead of shifting the following digits left. The value handed to `onChange`
 * is the digits joined together; an external `value` change (e.g. a reset)
 * re-syncs the boxes.
 */
const OtpCodeInput = ({
  length,
  value,
  onChange,
  error,
  disabled,
  ariaLabel,
  describedBy
}) => {
  const refs = useRef([]);
  const [boxes, setBoxes] = useState(() => splitToBoxes(value, length));
  // Tracks the value this component last emitted, so a re-render carrying our
  // own (position-collapsed) value back does not overwrite the box positions;
  // only a genuinely external value change re-syncs.
  const lastEmitted = useRef(value);

  useEffect(() => {
    if (value !== lastEmitted.current) {
      setBoxes(splitToBoxes(value, length));
      lastEmitted.current = value;
    }
  }, [value, length]);

  const commit = (next) => {
    setBoxes(next);
    const emitted = next.join("").slice(0, length);
    lastEmitted.current = emitted;
    onChange(emitted);
  };

  const focusBox = (i) => {
    const el = refs.current[Math.max(0, Math.min(i, length - 1))];
    if (el) {
      el.focus();
      el.select();
    }
  };

  const handleChange = (i, e) => {
    const raw = e.target.value.replace(/\D/g, "");
    const next = boxes.slice();

    if (raw.length > 1) {
      // Autofill / multi-char: spread across boxes starting at this one.
      let idx = i;
      for (const ch of raw.split("")) {
        if (idx >= length) break;
        next[idx] = ch;
        idx += 1;
      }
      commit(next);
      focusBox(idx);
      return;
    }

    next[i] = raw;
    commit(next);
    if (raw) focusBox(i + 1);
  };

  const handleKeyDown = (i, e) => {
    if (e.key === "Backspace") {
      const next = boxes.slice();
      if (boxes[i]) {
        next[i] = "";
        commit(next);
      } else if (i > 0) {
        next[i - 1] = "";
        commit(next);
        focusBox(i - 1);
      }
      e.preventDefault();
    } else if (e.key === "ArrowLeft") {
      e.preventDefault();
      focusBox(i - 1);
    } else if (e.key === "ArrowRight") {
      e.preventDefault();
      focusBox(i + 1);
    }
  };

  const handlePaste = (e) => {
    const pasted = (e.clipboardData.getData("text") || "")
      .replace(/\D/g, "")
      .slice(0, length);
    if (!pasted) return;
    e.preventDefault();
    commit(splitToBoxes(pasted, length));
    focusBox(pasted.length);
  };

  return (
    <div
      className="otp-code"
      onPaste={handlePaste}
      role="group"
      aria-label={ariaLabel}
    >
      {boxes.map((digit, i) => (
        <input
          key={i}
          ref={(el) => {
            refs.current[i] = el;
          }}
          id={i === 0 ? "otp-code-0" : undefined}
          className="otp-code-box"
          type="text"
          inputMode="numeric"
          autoComplete={i === 0 ? "one-time-code" : "off"}
          maxLength={1}
          value={digit}
          disabled={disabled}
          aria-invalid={Boolean(error)}
          aria-describedby={describedBy}
          aria-label={`Digit ${i + 1}`}
          onChange={(e) => handleChange(i, e)}
          onKeyDown={(e) => handleKeyDown(i, e)}
          onFocus={(e) => e.target.select()}
        />
      ))}
    </div>
  );
};

OtpCodeInput.defaultProps = {
  length: 6,
  value: "",
  error: false,
  disabled: false,
  ariaLabel: "One-time code",
  describedBy: undefined
};

OtpCodeInput.propTypes = {
  length: PropTypes.number,
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  error: PropTypes.bool,
  disabled: PropTypes.bool,
  ariaLabel: PropTypes.string,
  // id of the element describing the current error, wired to each box so a
  // screen reader announces the reason along with the invalid state.
  describedBy: PropTypes.string
};

export default OtpCodeInput;
